// Package imports
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import backend, { HttpBackendOptions, RequestCallback } from 'i18next-http-backend';

// Custom imports
import axiosInstance from '@helpers/Axios';
import { errorLogger } from '@utils/axios/ErrorLogging';

let usePodName = '';

interface LocaleBundle {
  actions?: Record<string, string>;
  labels?: Record<string, string>;
  message?: Record<string, string>;
  tooltip?: Record<string, string>;
}

const constructRequiredJson = (data: LocaleBundle) => {
  return { ...data.actions, ...data.labels, ...data.message, ...data.tooltip };
};

const loadLocaleBundle = async (locale: string) => {
  if (!locale) {
    return undefined;
  }
  let url = '/translate/get';
  if (usePodName) {
    url += `?podName=${usePodName}`;
  }

  return axiosInstance()
    .get(url, { params: { lang: locale, appVersion: 'V2' } })
    .then(response => {
      return constructRequiredJson(response.data);
    })
    .catch(error => {
      errorLogger(error, false, 'Error loading locale bundle');
    });
};

const backendOptions = {
  loadPath: '{{lng}}|{{ns}}',
  request: (options: HttpBackendOptions, url: string, payload: string | Record<string, never>, callback: RequestCallback) => {
    try {
      const [lng] = url.split('|');
      loadLocaleBundle(lng).then(data => {
        callback(null, {
          data: data ?? {},
          status: 200
        });
      });
    } catch (e) {
      errorLogger(e, false, 'Error loading locale bundle');
      callback(null, {
        data: '',
        status: 500
      });
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(backend)
  .init({
    backend: backendOptions,
    fallbackLng: 'en',
    debug: false,
    load: 'languageOnly',
    ns: ['translations'],
    defaultNS: 'translations',
    keySeparator: false,
    interpolation: {
      escapeValue: false,
      formatSeparator: ','
    },
    react: {
      transKeepBasicHtmlNodesFor: ['br', 'b', 'strong', 'i', 'p', 'a'],
      useSuspense: true
    }
  });

export const resetI18 = (podName: string) => {
  usePodName = podName;
  i18n
    .use(LanguageDetector)
    .use(backend)
    .init({
      backend: backendOptions,
      fallbackLng: 'en',
      debug: false,
      load: 'languageOnly',
      ns: ['translations'],
      defaultNS: 'translations',
      keySeparator: false,
      interpolation: {
        escapeValue: false,
        formatSeparator: ','
      },
      react: {
        transKeepBasicHtmlNodesFor: ['br', 'b', 'strong', 'i', 'p', 'a'],
        useSuspense: true
      }
    });
};

export default i18n;
